import React, { useState, useEffect, useMemo } from 'react';
import './Dashboard.css';

// mercury-backend (임포트이력, 백테스트 결과, 실제 체결내역)
const MB_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '/mercury-backend';

// ─────────────────────────────────────────
// 공통 타입
// ─────────────────────────────────────────
interface ImportHistoryItem {
  id: number; startTime: string; endTime: string; label: string; isSelected: boolean;
}
interface BinanceFuturesOrderRecord {
  id: number; symbol: string; trade_id: number; order_id: number;
  side: string; position_side: string; price: number | null; qty: number | null;
  quote_qty: number | null; realized_pnl: number | null; commission: number | null;
  commission_asset: string; trade_timestamp: string;
}

// ─────────────────────────────────────────
// 백테스트 응답 타입
// ─────────────────────────────────────────
interface TradeJSON {
  side: string; entryTimeMs: number; pct: number; leverage: number; pnl: number; fee: number;
}
interface RunResultJSON {
  label: string; takeProfit: number; stopLoss: number; leverage: number;
  trades: TradeJSON[]; wins: number; losses: number;
  totalPnl: number; winRate: number; finalAsset: number; initialUsdt: number;
}
interface PromotionJSON {
  timeMs: number; fromLabel: string; toLabel: string; liveScore: number; shadowScore: number;
}
interface ParamMgrJSON extends RunResultJSON {
  promotions: PromotionJSON[];
}
interface StratJSON {
  name: string; live: RunResultJSON; shadows: RunResultJSON[]; paramMgr: ParamMgrJSON;
}
interface BacktestResponseJSON {
  symbol: string; startMs: number; endMs: number; initialUsdt: number; strategies: StratJSON[];
}

// ─────────────────────────────────────────
// 스타일 헬퍼
// ─────────────────────────────────────────
const fmtNum = (v: number | null | undefined, d = 4) =>
  v != null ? v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d }) : '-';
const fmtPnl = (v: number | null | undefined) =>
  v != null ? `${v > 0 ? '+' : ''}${v.toFixed(4)}` : '-';
const pnlColor = (v: number | null | undefined) =>
  v == null ? '#000' : v > 0 ? '#dc3545' : v < 0 ? '#0d6efd' : '#888';
const fmtTime = (t?: string) => t ? t.replace('T', ' ').replace('Z', '').slice(0, 19) : '-';
const fmtMs = (ms: number) => new Date(ms).toISOString().replace('T', ' ').slice(0, 16) + ' UTC';

const thS: React.CSSProperties = {
  padding: '4px 8px', fontWeight: 600, border: '1px solid #dee2e6',
  backgroundColor: '#f0f4f8', whiteSpace: 'nowrap', fontSize: 11, textAlign: 'center', color: '#212529',
};
const tdS: React.CSSProperties = {
  padding: '4px 8px', border: '1px solid #dee2e6', fontSize: 11, whiteSpace: 'nowrap', color: '#212529',
};

const stratLabel: Record<string, string> = {
  bidirectional: '양방향거래',
  leverage: '레버리지 ATR',
  momentum: '모멘텀',
  vwap: 'VWAP 평균회귀',
};

// ─────────────────────────────────────────
// 단일 RunResult 카드
// ─────────────────────────────────────────
const ResultCard: React.FC<{ result: RunResultJSON; highlight?: boolean; badge?: string }> = ({ result, highlight, badge }) => {
  const [open, setOpen] = useState(false);
  const total = result.wins + result.losses;
  return (
    <div style={{
      border: `2px solid ${highlight ? '#0d6efd' : '#dee2e6'}`,
      borderRadius: 8, padding: '12px 14px', minWidth: 200, flex: '1 1 200px',
      backgroundColor: highlight ? '#f0f6ff' : '#fafafa', position: 'relative',
    }}>
      {badge && (
        <span style={{
          position: 'absolute', top: 6, right: 8, fontSize: 10,
          backgroundColor: highlight ? '#0d6efd' : '#6c757d',
          color: '#fff', borderRadius: 4, padding: '1px 6px',
        }}>{badge}</span>
      )}
      <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 4, paddingRight: 40 }}>{result.label}</div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 10, color: '#aaa' }}>승률</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: result.winRate >= 50 ? '#dc3545' : '#0d6efd' }}>
            {total === 0 ? '-' : `${result.winRate.toFixed(1)}%`}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#aaa' }}>총거래</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#212529' }}>{total}</div>
        </div>
      </div>
      <div style={{ fontSize: 12, marginBottom: 2 }}>
        총수익: <span style={{ fontWeight: 600, color: pnlColor(result.totalPnl) }}>
          {result.totalPnl > 0 ? '+' : ''}{result.totalPnl.toFixed(2)} USDT
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 6 }}>
        {result.initialUsdt.toFixed(0)} → <strong>{result.finalAsset.toFixed(2)}</strong> USDT
        {' '}({result.totalPnl >= 0 ? '+' : ''}{(result.totalPnl / result.initialUsdt * 100).toFixed(2)}%)
      </div>
      <div style={{ fontSize: 10, color: '#999', marginBottom: 6 }}>
        TP={result.takeProfit}%  SL={result.stopLoss}%  Lev={result.leverage}x
      </div>
      {result.trades.length > 0 && (
        <button
          onClick={() => setOpen(v => !v)}
          style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #ced4da', borderRadius: 4, cursor: 'pointer', backgroundColor: '#fff' }}
        >
          {open ? '▲ 체결 숨기기' : `▼ 체결 ${result.trades.length}건 보기`}
        </button>
      )}
      {open && (
        <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={thS}>시각</th><th style={thS}>방향</th>
                <th style={thS}>Lev</th><th style={thS}>수익률</th>
                <th style={thS}>손익 (USDT)</th><th style={thS}>수수료 (USDT)</th>
                <th style={thS}>누적손익 (USDT)</th><th style={thS}>누적수익률</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let cumPnl = 0;
                return result.trades.map((t, i) => {
                  cumPnl += t.pnl;
                  const cumPct = result.initialUsdt > 0 ? cumPnl / result.initialUsdt * 100 : 0;
                  return (
                    <tr key={i}>
                      <td style={tdS}>{fmtMs(t.entryTimeMs).slice(5, 16)}</td>
                      <td style={{ ...tdS, color: t.side === 'LONG' ? '#dc3545' : '#0d6efd', fontWeight: 600, textAlign: 'center' }}>{t.side}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{t.leverage}x</td>
                      <td style={{ ...tdS, textAlign: 'right', color: pnlColor(t.pct) }}>{t.pct > 0 ? '+' : ''}{t.pct.toFixed(2)}%</td>
                      <td style={{ ...tdS, textAlign: 'right', color: pnlColor(t.pnl) }}>{t.pnl > 0 ? '+' : ''}{t.pnl.toFixed(2)}</td>
                      <td style={{ ...tdS, textAlign: 'right', color: '#6c757d' }}>{(t.fee ?? 0).toFixed(4)}</td>
                      <td style={{ ...tdS, textAlign: 'right', color: pnlColor(cumPnl) }}>{cumPnl > 0 ? '+' : ''}{cumPnl.toFixed(2)}</td>
                      <td style={{ ...tdS, textAlign: 'right', color: pnlColor(cumPct) }}>{cumPct > 0 ? '+' : ''}{cumPct.toFixed(2)}%</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────
// ParamMgr 섹션
// ─────────────────────────────────────────
const ParamMgrSection: React.FC<{ pm: ParamMgrJSON; initialUsdt: number }> = ({ pm, initialUsdt }) => {
  const [open, setOpen] = useState(false);
  const total = pm.wins + pm.losses;
  return (
    <div style={{ border: '1px solid #ced4da', borderRadius: 8, padding: '12px 14px', backgroundColor: '#f8f9fa' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: pm.promotions.length > 0 ? 10 : 0, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: '#495057' }}>ParamMgr 시뮬레이션</span>
        {pm.promotions.length === 0 ? (
          <span style={{ fontSize: 12, color: '#6c757d' }}>전환이 일어나지 않았습니다</span>
        ) : (
          <>
            <span style={{ fontSize: 11, color: '#6c757d' }}>파라미터 전환 {pm.promotions.length}회</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: pnlColor(pm.totalPnl) }}>
              {pm.totalPnl > 0 ? '+' : ''}{pm.totalPnl.toFixed(2)} USDT
              ({total === 0 ? '-' : `${pm.winRate.toFixed(1)}% 승률`})
            </span>
            <span style={{ fontSize: 11, color: '#6c757d' }}>
              {initialUsdt.toFixed(0)} → {pm.finalAsset.toFixed(2)} USDT
            </span>
          </>
        )}
      </div>
      {pm.promotions.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {pm.promotions.map((p, i) => (
            <div key={i} style={{
              fontSize: 11, padding: '4px 10px', backgroundColor: '#fff3cd',
              border: '1px solid #ffc107', borderRadius: 4, marginBottom: 4,
              display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
            }}>
              <span>▶ <strong>{fmtMs(p.timeMs).slice(5, 16)}</strong></span>
              <span style={{ color: '#6c757d' }}>{p.fromLabel}</span>
              <span>→</span>
              <span style={{ color: '#0d6efd', fontWeight: 600 }}>{p.toLabel}</span>
              <span style={{ color: '#888' }}>(live {p.liveScore.toFixed(2)}% → shadow {p.shadowScore.toFixed(2)}%)</span>
            </div>
          ))}
        </div>
      )}
      {pm.promotions.length > 0 && pm.trades.length > 0 && (
        <>
          <button
            onClick={() => setOpen(v => !v)}
            style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #ced4da', borderRadius: 4, cursor: 'pointer', backgroundColor: '#fff', marginBottom: 6 }}
          >
            {open ? '▲ 체결 숨기기' : `▼ 체결 ${pm.trades.length}건 보기`}
          </button>
          {open && (
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={thS}>시각</th><th style={thS}>방향</th>
                    <th style={thS}>Lev</th><th style={thS}>수익률</th>
                    <th style={thS}>손익 (USDT)</th><th style={thS}>수수료 (USDT)</th>
                    <th style={thS}>누적손익 (USDT)</th><th style={thS}>누적수익률</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let promIdx = 0;
                    let cumPnl = 0;
                    const rows: React.ReactNode[] = [];
                    for (let i = 0; i < pm.trades.length; i++) {
                      const t = pm.trades[i];
                      while (promIdx < pm.promotions.length && t.entryTimeMs >= pm.promotions[promIdx].timeMs) {
                        const p = pm.promotions[promIdx];
                        rows.push(
                          <tr key={`promo-${promIdx}`}>
                            <td colSpan={8} style={{ ...tdS, backgroundColor: '#fff3cd', fontWeight: 600, color: '#856404', textAlign: 'center' }}>
                              ▶ [{fmtMs(p.timeMs).slice(5, 16)}] {p.fromLabel} → {p.toLabel}
                            </td>
                          </tr>
                        );
                        promIdx++;
                      }
                      cumPnl += t.pnl;
                      const cumPct = initialUsdt > 0 ? cumPnl / initialUsdt * 100 : 0;
                      rows.push(
                        <tr key={i}>
                          <td style={tdS}>{fmtMs(t.entryTimeMs).slice(5, 16)}</td>
                          <td style={{ ...tdS, color: t.side === 'LONG' ? '#dc3545' : '#0d6efd', fontWeight: 600, textAlign: 'center' }}>{t.side}</td>
                          <td style={{ ...tdS, textAlign: 'center' }}>{t.leverage}x</td>
                          <td style={{ ...tdS, textAlign: 'right', color: pnlColor(t.pct) }}>{t.pct > 0 ? '+' : ''}{t.pct.toFixed(2)}%</td>
                          <td style={{ ...tdS, textAlign: 'right', color: pnlColor(t.pnl) }}>{t.pnl > 0 ? '+' : ''}{t.pnl.toFixed(2)}</td>
                          <td style={{ ...tdS, textAlign: 'right', color: '#6c757d' }}>{(t.fee ?? 0).toFixed(4)}</td>
                          <td style={{ ...tdS, textAlign: 'right', color: pnlColor(cumPnl) }}>{cumPnl > 0 ? '+' : ''}{cumPnl.toFixed(2)}</td>
                          <td style={{ ...tdS, textAlign: 'right', color: pnlColor(cumPct) }}>{cumPct > 0 ? '+' : ''}{cumPct.toFixed(2)}%</td>
                        </tr>
                      );
                    }
                    return rows;
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────
// 단일 전략 결과 패널
// ─────────────────────────────────────────
const StrategyPanel: React.FC<{ strat: StratJSON; initialUsdt: number }> = ({ strat, initialUsdt }) => (
  <div style={{ padding: '0 0 20px 0' }}>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
      <ResultCard result={strat.live} highlight badge="LIVE" />
      {strat.shadows.map((s, i) => (
        <ResultCard key={i} result={s} badge={`Shadow #${i + 1}`} />
      ))}
    </div>
    <ParamMgrSection pm={strat.paramMgr} initialUsdt={initialUsdt} />
  </div>
);

// ─────────────────────────────────────────
// 메인 페이지
// ─────────────────────────────────────────
interface BtRunRecord {
  id: number;
  created_at: string;
  result: BacktestResponseJSON;
}

const AnalyCryptoBinancePage: React.FC = () => {
  // ── 공통: 시간 범위 ──
  const [importHistory, setImportHistory] = useState<ImportHistoryItem[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('');

  // ── 백테스트 ──
  const [symbol, setSymbol] = useState('XRPUSDT');
  const [strategy, setStrategy] = useState('all');
  const [btLoading, setBtLoading] = useState(false);
  const [btError, setBtError] = useState<string | null>(null);
  const [btRuns, setBtRuns] = useState<BtRunRecord[]>([]);
  const [activeRun, setActiveRun] = useState(0);
  const [activeStrat, setActiveStrat] = useState(0);

  // ── 실제 체결내역 ──
  const [orders, setOrders] = useState<BinanceFuturesOrderRecord[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [symbolFilter, setSymbolFilter] = useState('');

  const selectedItem = importHistory.find(h => h.id.toString() === selectedTimeRange);

  // 임포트 이력 로드
  useEffect(() => {
    fetch(`${MB_BASE}/bt/import-history?tableName=bt_binance_futures_trade`)
      .then(r => r.json())
      .then(res => {
        const h: ImportHistoryItem[] = (res.data || []).map((item: any) => ({
          id: item.id, startTime: item.start_time, endTime: item.end_time,
          label: `[${item.sequence_number}] ${item.start_time} ~ ${item.end_time}`,
          isSelected: item.is_selected || false,
        }));
        setImportHistory(h);
        if (h.length > 0) {
          const sel = h.find(x => x.isSelected) || h[0];
          setSelectedTimeRange(sel.id.toString());
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 실제 체결내역 로드
  useEffect(() => {
    setOrdersLoading(true);
    setOrdersError(null);
    fetch(`${MB_BASE}/bt/binance-futures-orders/history?limit=2000`)
      .then(r => r.json())
      .then(j => setOrders(j.data || []))
      .catch(err => setOrdersError(err instanceof Error ? err.message : 'Failed to fetch'))
      .finally(() => setOrdersLoading(false));
  }, []);

  // 백테스트 결과 자동 로드 (시간범위/심볼/전략 변경 시)
  useEffect(() => {
    if (!selectedItem) return;
    setBtLoading(true);
    setBtError(null);
    setBtRuns([]);
    const params = new URLSearchParams({
      symbol: symbol.toUpperCase(),
      strategy,
      start: selectedItem.startTime,
      end: selectedItem.endTime,
    });
    fetch(`${MB_BASE}/bt/binance-futures-backtest-result?${params}`)
      .then(r => r.json())
      .then((j: { success: boolean; data: BtRunRecord[] }) => {
        setBtRuns(j.data ?? []);
        setActiveRun(0);
        setActiveStrat(0);
      })
      .catch(err => setBtError(err instanceof Error ? err.message : 'Failed to fetch'))
      .finally(() => setBtLoading(false));
  }, [selectedItem, symbol, strategy]); // eslint-disable-line react-hooks/exhaustive-deps

  // 선택된 시간 범위로 체결내역 필터링
  const filteredOrders = useMemo(() => {
    let data = orders;
    if (selectedItem) {
      const s = Date.parse(selectedItem.startTime.endsWith('Z') ? selectedItem.startTime : selectedItem.startTime + 'Z');
      const e = Date.parse(selectedItem.endTime.endsWith('Z') ? selectedItem.endTime : selectedItem.endTime + 'Z');
      data = data.filter(r => {
        const t = Date.parse(r.trade_timestamp.replace(' ', 'T') + 'Z');
        return t >= s && t <= e;
      });
    }
    if (symbolFilter.trim()) {
      const sym = symbolFilter.trim().toUpperCase();
      data = data.filter(r => r.symbol === sym);
    }
    return data;
  }, [orders, selectedItem, symbolFilter]);

  // 심볼별 요약
  const bySymbol = useMemo(() => {
    const map = new Map<string, { count: number; pnl: number; commission: number }>();
    for (const r of filteredOrders) {
      const prev = map.get(r.symbol) || { count: 0, pnl: 0, commission: 0 };
      map.set(r.symbol, { count: prev.count + 1, pnl: prev.pnl + (r.realized_pnl ?? 0), commission: prev.commission + (r.commission ?? 0) });
    }
    return Array.from(map.entries()).map(([sym, v]) => ({ symbol: sym, ...v })).sort((a, b) => b.pnl - a.pnl);
  }, [filteredOrders]);

  const totalPnl = filteredOrders.reduce((s, r) => s + (r.realized_pnl ?? 0), 0);
  const totalCommission = filteredOrders.reduce((s, r) => s + (r.commission ?? 0), 0);

  return (
    <div className="dashboard-page">
      <div className="dashboard-sections">
        <div className="dashboard-section full-width">
          <div className="section-header">
            <h2>Binance Futures 분석</h2>
          </div>
          <div className="section-content">
            <div style={{ width: '100%', maxWidth: 1600, margin: '0 auto' }}>

              {/* ── 1. 시간 범위 + 백테스트 컨트롤 ── */}
              <div style={{ marginBottom: 20, padding: '14px 16px', border: '1px solid #dee2e6', borderRadius: 8, backgroundColor: '#f8f9fa' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#495057' }}>시간 범위 선택</h4>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 3 }}>시간 범위</div>
                    <select
                      value={selectedTimeRange}
                      onChange={e => setSelectedTimeRange(e.target.value)}
                      style={{ padding: '5px 8px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13, minWidth: 300 }}
                    >
                      <option value="">선택</option>
                      {importHistory.map(item => (
                        <option key={item.id} value={item.id.toString()}>{item.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 3 }}>심볼</div>
                    <input
                      value={symbol}
                      onChange={e => setSymbol(e.target.value.toUpperCase())}
                      style={{ width: 110, padding: '5px 8px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13 }}
                      placeholder="XRPUSDT"
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 3 }}>전략</div>
                    <select
                      value={strategy}
                      onChange={e => setStrategy(e.target.value)}
                      style={{ padding: '5px 8px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13 }}
                    >
                      <option value="all">전체 (all)</option>
                      <option value="bidirectional">양방향거래</option>
                      <option value="leverage">레버리지 ATR</option>
                      <option value="momentum">모멘텀</option>
                      <option value="vwap">VWAP 평균회귀</option>
                    </select>
                  </div>
                  <button
                    disabled
                    style={{
                      padding: '6px 18px',
                      backgroundColor: '#adb5bd',
                      color: '#fff', border: 'none', borderRadius: 4, fontSize: 13,
                      cursor: 'not-allowed', fontWeight: 600,
                    }}
                  >
                    ▶ 백테스트 실행 (준비 중)
                  </button>
                </div>
              </div>

              {/* ── 2. 백테스트 결과 ── */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <h4 style={{ margin: 0, color: '#fff' }}>
                    백테스트 결과
                    {btRuns.length > 0 && (
                      <span style={{ fontSize: 12, fontWeight: 400, color: 'rgba(255,255,255,0.6)', marginLeft: 8 }}>
                        {btRuns.length}회 실행
                      </span>
                    )}
                  </h4>
                  <button
                    onClick={() => {
                      if (!selectedItem) return;
                      setBtLoading(true);
                      setBtError(null);
                      setBtRuns([]);
                      const params = new URLSearchParams({
                        symbol: symbol.toUpperCase(),
                        strategy,
                        start: selectedItem.startTime,
                        end: selectedItem.endTime,
                      });
                      fetch(`${MB_BASE}/bt/binance-futures-backtest-result?${params}`)
                        .then(r => r.json())
                        .then((j: { success: boolean; data: BtRunRecord[] }) => {
                          setBtRuns(j.data ?? []);
                          setActiveRun(0);
                          setActiveStrat(0);
                        })
                        .catch(err => setBtError(err instanceof Error ? err.message : 'Failed to fetch'))
                        .finally(() => setBtLoading(false));
                    }}
                    disabled={!selectedItem || btLoading}
                    style={{
                      padding: '3px 10px', fontSize: 12, cursor: selectedItem && !btLoading ? 'pointer' : 'not-allowed',
                      border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4,
                      backgroundColor: 'transparent', color: '#fff', opacity: selectedItem && !btLoading ? 1 : 0.4,
                    }}
                  >
                    ↻ 새로고침
                  </button>
                </div>

                {btError && (
                  <div style={{ padding: '10px 14px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
                    ❌ {btError}
                  </div>
                )}

                {btLoading && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6c757d', fontSize: 13, border: '1px solid #dee2e6', borderRadius: 4 }}>
                    로딩중...
                  </div>
                )}

                {!btLoading && !btError && !selectedItem && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6c757d', fontSize: 13, border: '1px solid #dee2e6', borderRadius: 4 }}>
                    시간 범위를 선택하세요.
                  </div>
                )}

                {!btLoading && !btError && selectedItem && btRuns.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6c757d', fontSize: 13, border: '1px solid #dee2e6', borderRadius: 4 }}>
                    해당 기간의 백테스트 결과가 없습니다. CLI로 백테스트를 실행해 주세요.
                  </div>
                )}

                {btRuns.length > 0 && (() => {
                  const run = btRuns[activeRun];
                  return (
                    <div>
                      {/* 실행 회차 선택 탭 */}
                      {btRuns.length > 1 && (
                        <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
                          {btRuns.map((r, i) => (
                            <button
                              key={r.id}
                              onClick={() => { setActiveRun(i); setActiveStrat(0); }}
                              style={{
                                padding: '4px 12px', border: '1px solid #ced4da', borderRadius: 4,
                                cursor: 'pointer', fontSize: 11,
                                fontWeight: activeRun === i ? 700 : 400,
                                backgroundColor: activeRun === i ? '#495057' : '#f8f9fa',
                                color: activeRun === i ? '#fff' : '#495057',
                              }}
                            >
                              Run #{btRuns.length - i}
                              <span style={{ marginLeft: 5, fontSize: 10, opacity: 0.75 }}>{r.created_at.slice(5, 16)}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* 선택된 실행 결과 */}
                      <div style={{ marginBottom: 12, padding: '10px 14px', backgroundColor: '#e8f4fd', border: '1px solid #bee5f8', borderRadius: 6 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#212529' }}>{run.result.symbol}</span>
                        <span style={{ color: '#6c757d', fontSize: 12, marginLeft: 8 }}>
                          {fmtMs(run.result.startMs)} ~ {fmtMs(run.result.endMs)}
                        </span>
                        <span style={{ color: '#6c757d', fontSize: 12, marginLeft: 8 }}>
                          초기자본 {run.result.initialUsdt.toLocaleString()} USDT
                        </span>
                        <span style={{ color: '#aaa', fontSize: 11, marginLeft: 8 }}>{run.created_at}</span>
                      </div>

                      {run.result.strategies.length > 1 && (
                        <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '2px solid #dee2e6' }}>
                          {run.result.strategies.map((s, i) => (
                            <button
                              key={i}
                              onClick={() => setActiveStrat(i)}
                              style={{
                                padding: '6px 14px', border: 'none', borderRadius: '4px 4px 0 0',
                                cursor: 'pointer', fontSize: 12, fontWeight: activeStrat === i ? 700 : 400,
                                backgroundColor: activeStrat === i ? '#0d6efd' : '#f0f4f8',
                                color: activeStrat === i ? '#fff' : '#495057',
                                borderBottom: activeStrat === i ? '2px solid #0d6efd' : '2px solid transparent',
                                marginBottom: -2,
                              }}
                            >
                              {stratLabel[s.name] || s.name}
                              {(() => {
                                const best = [...s.shadows, s.live].reduce((a, b) => b.totalPnl > a.totalPnl ? b : a, s.live);
                                const color = activeStrat === i ? '#cce' : (best.totalPnl > 0 ? '#dc3545' : '#0d6efd');
                                return (
                                  <span style={{ marginLeft: 6, fontSize: 10, color }}>
                                    {best.totalPnl > 0 ? '+' : ''}{best.totalPnl.toFixed(1)}
                                  </span>
                                );
                              })()}
                            </button>
                          ))}
                        </div>
                      )}

                      {run.result.strategies[activeStrat] && (
                        <StrategyPanel strat={run.result.strategies[activeStrat]} initialUsdt={run.result.initialUsdt} />
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* ── 3. 실제 체결내역 ── */}
              <div style={{ marginBottom: 8, padding: '14px 16px', border: '1px solid #dee2e6', borderRadius: 8, backgroundColor: '#f8f9fa' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <h4 style={{ margin: 0, color: '#495057' }}>실제 체결내역</h4>
                  {ordersLoading && <span style={{ fontSize: 12, color: '#6c757d' }}>로딩중...</span>}
                  <input
                    type="text" value={symbolFilter} onChange={e => setSymbolFilter(e.target.value)}
                    placeholder="심볼 필터 (예: BTCUSDT)"
                    style={{ padding: '4px 8px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 12, width: 160, marginLeft: 'auto' }}
                  />
                </div>

                {ordersError && (
                  <div style={{ padding: 8, backgroundColor: '#f8d7da', color: '#721c24', borderRadius: 4, marginBottom: 10, fontSize: 12 }}>{ordersError}</div>
                )}

                {/* 요약 카드 */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                  {[
                    { label: '총 체결', value: `${filteredOrders.length.toLocaleString()}건` },
                    { label: '총 실현손익', value: `${totalPnl > 0 ? '+' : ''}${totalPnl.toFixed(4)} USDT`, color: pnlColor(totalPnl) },
                    { label: '총 수수료', value: `${totalCommission.toFixed(4)} USDT`, color: '#6c757d' },
                    { label: '순손익', value: `${(totalPnl - Math.abs(totalCommission)) > 0 ? '+' : ''}${(totalPnl - Math.abs(totalCommission)).toFixed(4)} USDT`, color: pnlColor(totalPnl - Math.abs(totalCommission)) },
                  ].map(card => (
                    <div key={card.label} style={{ padding: '10px 14px', backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: 8, minWidth: 140 }}>
                      <div style={{ fontSize: 10, color: '#6c757d', marginBottom: 3 }}>{card.label}</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: card.color || '#212529' }}>{card.value}</div>
                    </div>
                  ))}
                </div>

                {/* 심볼별 요약 */}
                {bySymbol.length > 0 && (
                  <div style={{ marginBottom: 16, overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead><tr>
                        <th style={thS}>심볼</th><th style={thS}>체결</th>
                        <th style={thS}>실현손익</th><th style={thS}>수수료</th><th style={thS}>순손익</th>
                      </tr></thead>
                      <tbody>
                        {bySymbol.map(row => (
                          <tr key={row.symbol}>
                            <td style={{ ...tdS, fontWeight: 600 }}>{row.symbol}</td>
                            <td style={{ ...tdS, textAlign: 'right' }}>{row.count.toLocaleString()}</td>
                            <td style={{ ...tdS, textAlign: 'right', color: pnlColor(row.pnl) }}>{fmtPnl(row.pnl)}</td>
                            <td style={{ ...tdS, textAlign: 'right', color: '#6c757d' }}>{row.commission.toFixed(4)}</td>
                            <td style={{ ...tdS, textAlign: 'right', color: pnlColor(row.pnl - Math.abs(row.commission)) }}>
                              {fmtPnl(row.pnl - Math.abs(row.commission))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* 체결 목록 */}
                {filteredOrders.length === 0 && !ordersLoading ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6c757d', fontSize: 13, border: '1px solid #dee2e6', borderRadius: 4 }}>
                    데이터 없음
                  </div>
                ) : (
                  <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={thS}>심볼</th><th style={thS}>방향</th><th style={thS}>포지션</th>
                        <th style={thS}>가격</th><th style={thS}>수량</th><th style={thS}>거래금액</th>
                        <th style={thS}>실현손익</th><th style={thS}>수수료</th><th style={thS}>시각 (UTC)</th>
                      </tr></thead>
                      <tbody>
                        {filteredOrders.map(r => (
                          <tr key={r.id}>
                            <td style={{ ...tdS, fontWeight: 600 }}>{r.symbol}</td>
                            <td style={{ ...tdS, color: r.side === 'BUY' ? '#dc3545' : '#0d6efd', fontWeight: 600, textAlign: 'center' }}>{r.side}</td>
                            <td style={{ ...tdS, textAlign: 'center', color: '#6c757d' }}>{r.position_side}</td>
                            <td style={{ ...tdS, textAlign: 'right' }}>{fmtNum(r.price, 2)}</td>
                            <td style={{ ...tdS, textAlign: 'right' }}>{fmtNum(r.qty, 4)}</td>
                            <td style={{ ...tdS, textAlign: 'right' }}>{fmtNum(r.quote_qty, 2)}</td>
                            <td style={{ ...tdS, textAlign: 'right', color: pnlColor(r.realized_pnl) }}>{fmtPnl(r.realized_pnl)}</td>
                            <td style={{ ...tdS, textAlign: 'right', color: '#6c757d' }}>
                              {r.commission != null ? r.commission.toFixed(6) : '-'} {r.commission_asset}
                            </td>
                            <td style={tdS}>{fmtTime(r.trade_timestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyCryptoBinancePage;
